import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Calculator, Menu, X, ArrowLeft } from 'lucide-react'
import { useState } from 'react'
import LegalDisclaimer from './LegalDisclaimer.tsx'
import NextCalculators from './NextCalculators.tsx'
import SchemaManager from './SchemaManager.tsx'
import SEO from './SEO.tsx'

interface LayoutProps {
  children: React.ReactNode
}

const navItems = [
  { path: '/ai-salary-gap', label: 'Salary Gap', priority: 1 },
  { path: '/skills-roi', label: 'Skills ROI', priority: 2 },
  { path: '/ai-productivity', label: 'Productivity', priority: 3 },
  { path: '/hire-vs-ai', label: 'Hire vs AI', priority: 4 },
  { path: '/automation-savings', label: 'Automation', priority: 5 },
  { path: '/displacement-risk', label: 'Displacement Risk', priority: 6 },
  { path: '/lifetime-earnings', label: 'Lifetime Earnings', priority: 7 },
  { path: '/hub', label: 'All Calculators', priority: 8 },
  { path: '/glossary', label: 'Glossary', priority: 9 }
]

export default function Layout({ children }: LayoutProps) {
  const [mobileOpen, setMobileOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const showBack = location.pathname !== '/'

  return (
    <div className="min-h-screen flex flex-col">
      <SEO />
      <SchemaManager />
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-md"
      >
        Skip to main content
      </a>
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              {showBack && (
                <button
                  onClick={() => navigate(-1)}
                  className="p-2 rounded-md text-slate-600 hover:bg-slate-100 lg:hidden"
                  aria-label="Go back"
                >
                  <ArrowLeft className="w-5 h-5" aria-hidden="true" />
                </button>
              )}
              <Link to="/" className="flex items-center gap-2 text-primary-700 hover:text-primary-800" aria-label="The AI Economy Suite home">
                <Calculator className="w-7 h-7" aria-hidden="true" />
                <span className="font-bold text-xl">The AI Economy Suite</span>
              </Link>
            </div>
            <nav className="hidden lg:flex items-center gap-0.5">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-2 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                    location.pathname === item.path
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                  aria-current={location.pathname === item.path ? 'page' : undefined}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
            <button
              className="lg:hidden p-2 rounded-md text-slate-600 hover:bg-slate-100"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="lg:hidden border-t border-slate-200 bg-white">
            <div className="px-2 pt-2 pb-3 space-y-1">
              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                className="block px-3 py-2 rounded-md text-base font-medium text-slate-700 hover:bg-slate-50"
              >
                Home
              </Link>
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileOpen(false)}
                  className={`block px-3 py-2 rounded-md text-base font-medium ${
                    location.pathname === item.path
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-slate-700 hover:bg-slate-50'
                  }`}
                  aria-current={location.pathname === item.path ? 'page' : undefined}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </header>
      <main id="main-content" className="flex-1" tabIndex={-1}>{children}</main>
      <NextCalculators />
      <LegalDisclaimer />
      <footer className="bg-white border-t border-slate-200 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2 text-slate-700">
              <Calculator className="w-5 h-5" aria-hidden="true" />
              <span className="font-semibold">The AI Economy Suite</span>
            </div>
            <div className="flex flex-col md:flex-row items-center gap-2 text-sm text-slate-500">
              <span>Built for the AI income and career vertical.</span>
              <Link to="/methodology" className="text-primary-600 hover:underline">Methodology & data sources</Link>
              <Link to="/privacy" className="text-primary-600 hover:underline">Privacy</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
